import { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog/dialog";
import { TextAreaField } from "@/components/ui/form/fields/text-area-field";

import * as Model from "../model";
import { LabelWithText } from "./label-with-text";

import type * as Repository from "../../_repository";

export type Props = {
	option: Repository.Query.QueryData.OptionPmsList.Option;
	onCloseCallback: () => void;
};

export const UpdateMemoForOptionPms = ({ option, onCloseCallback }: Props) => {
	/**
	 * 初期化
	 */
	const {
		//
		viewData,
		//
		viewModel,
		// メモの変更
		onChangeMemo,
		//
		// メモの更新
		onUpdateMemo,
	} = Model.UseCase.useInitialized({ option });

	const [open, setOpen] = useState(true);
	/**
	 * ダイアログの表示制御
	 */
	const onOpenChange = useCallback((value: boolean) => {
		setOpen(value);
	}, []);
	useEffect(() => {
		if (open) return;

		/**
		 * ダイアログが閉じた時の処理
		 */
		onCloseCallback();
	}, [onCloseCallback, open]);

	/**
	 * メモの変更
	 */
	const handleChangeMemo = useCallback(
		(value: string) => {
			onChangeMemo(value);
		},
		[onChangeMemo],
	);

	/**
	 *  メモの更新
	 */
	const handleClickUpdateMemo = useCallback(() => {
		const successCallback = () => onOpenChange(false);
		onUpdateMemo(successCallback);
	}, [onOpenChange, onUpdateMemo]);

	const disabled = useMemo(() => !!viewModel.memo.error, [viewModel.memo.error]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="md:max-w-xl">
				<DialogHeader>
					<DialogTitle>オプションの更新</DialogTitle>
				</DialogHeader>

				<div className="border-b border-zinc-300" />

				<div className="space-y-3 px-6">
					<LabelWithText label="日付" text={viewData.dateText} />

					<LabelWithText label="氏名" text={viewData.guestNameText} />

					<LabelWithText label="電話番号" text={viewData.guestTellText} />

					<LabelWithText label="オプション名" text={viewData.optionNameText} />

					<LabelWithText label="個数" text={viewData.amountText} />

					{Boolean(viewData.guestTotalNumberText) && (
						<div className="space-y-1">
							<LabelWithText label="合計人数" text={viewData.guestTotalNumberText} />
							<p className="text-sm text-brand">
								※合計人数と実際のオプション利用人数は異なる場合があります
							</p>
						</div>
					)}

					<LabelWithText label="宿泊プラン" text={viewData.planNameText} />

					<LabelWithText label="決済方法" text={viewData.paymentMethodText} />

					{viewData.questions.map(({ question, answer }, idx) => {
						const label = idx === 0 ? "質問" : "";
						const text = answer.map((answer) => <div key={answer}>{answer}</div>);

						return (
							<LabelWithText
								key={question}
								label={label}
								text={
									<div className="space-y-2 text-sm">
										<p className="font-semibold">{question}</p>
										<p>{text}</p>
									</div>
								}
							/>
						);
					})}

					<div className="space-y-4">
						<div className="space-y-1">
							<p>メモ</p>

							<TextAreaField
								rows={4}
								value={viewModel.memo.value}
								handleChange={handleChangeMemo}
								errorMessage={viewModel.memo.error}
							/>
						</div>

						<div className="text-center">
							<Button variant="default" type="button" onClick={handleClickUpdateMemo} disabled={disabled}>
								更新
							</Button>
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};
